import React from "react";
import { useNavigate } from "react-router-dom";
import type { TaxDocumentConnection } from "../../../../domain/types/tax-document";
import { resolveErrorMessage } from "../../../../shared/utils/save-flow";
import ConfirmMessageModal from "../../UI/ConfirmMessageModal";
import { openUrlFromServer, taxDocumentApi } from "./tax-document-api";

interface Props {
  companyId: number;
  connection: TaxDocumentConnection | null;
  onReload: () => void;
}

/**
 * 발급 연동 상태 안내.
 * - 미연동/재연동 필요 → 내 회사 정보(추가 서비스 연동)로 안내
 * - 연동됨 + 인증서 없음 → 세금계산서 발급용 공동인증서 등록 버튼
 *   등록 화면은 연동 서비스가 운영하는 보안 화면이라, 열기 전에 무엇이 뜨는지 먼저 알린다.
 */
export const TaxDocumentConnectionBanner: React.FC<Props> = ({ companyId, connection, onReload }) => {
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState("");

  if (!connection) return null;

  const box = "mb-5 rounded-[10px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-3 text-[13px] text-[#4285F4] flex items-center gap-3";

  if (!connection.connected) {
    return (
      <div className={box}>
        <span className="flex-1">
          {connection.needsReconnect
            ? "예전 홈택스 API 키 방식은 더 이상 발급에 쓰이지 않습니다. 홈택스 연동을 다시 저장하면 이 화면에서 바로 발급할 수 있습니다."
            : "홈택스 발급 연동 전입니다. 연동하면 이 화면에서 세금계산서·현금영수증을 국세청으로 바로 발급할 수 있습니다."}
        </span>
        <button
          onClick={() => navigate(`/company/${companyId}/info`)}
          className="shrink-0 px-3 h-[32px] rounded-[6px] bg-[#4285F4] text-white hover:bg-[#3367D6]"
        >
          연동하러 가기
        </button>
      </div>
    );
  }

  const handleCertificate = async () => {
    setNotice("");
    setError(null);
    try {
      await openUrlFromServer(() => taxDocumentApi.certificateUrl(companyId));
    } catch (e) {
      setError(resolveErrorMessage(e, "인증서 등록 화면을 열 수 없습니다."));
    }
  };

  return (
    <div className={box}>
      <span className="flex-1">
        홈택스 발급 연동됨 · {connection.autoIssue ? "입금 확인 시 자동 발급" : "수동 발급"}
        {" · "}
        {connection.certificateRegistered
          ? `세금계산서 공동인증서 등록됨${connection.certificateExpireDate ? ` (만료 ${connection.certificateExpireDate})` : ""}`
          : connection.certificateMessage ?? "세금계산서를 발급하려면 공동인증서를 등록해 주세요"}
        {error && <span className="block text-[#F24A4A] mt-1">{error}</span>}
      </span>
      <button
        onClick={() => setNotice(CERTIFICATE_NOTICE)}
        className="shrink-0 px-3 h-[32px] rounded-[6px] border border-[#4285F4] bg-white text-[#4285F4] hover:bg-[#E8EFFF]"
      >
        {connection.certificateRegistered ? "인증서 갱신" : "인증서 등록"}
      </button>
      <button onClick={onReload} className="shrink-0 px-2 h-[32px] text-[#4285F4] hover:text-[#3367D6]">
        상태 새로고침
      </button>
      {notice && <ConfirmMessageModal message={notice} setMessage={setNotice} submitFunction={handleCertificate} />}
    </div>
  );
};

const CERTIFICATE_NOTICE =
  "공동인증서 등록은 국세청 인증을 받은 연동 서비스의 보안 화면에서 진행됩니다. " +
  "확인을 누르면 새 창이 열리니, 사업자 공동인증서(전자세금계산서용 또는 범용)를 선택해 등록해 주세요. " +
  "등록을 마친 뒤 이 화면에서 '상태 새로고침'을 누르면 반영됩니다.";
